import {
    Component,
    ComponentInterface,
    Element,
    h,
    Host,
    Listen,
    Prop,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";
import serializeForm from "../../utils/serializeForm";
import api from "../../utils/api";
import waitForHydrated from "../../utils/waitForHydrated";

const baseClass = "ux-iws-search-results";

@Component({
    tag: "ux-iws-search-results",
    styleUrl: "search-results.scss",
})
export class SearchResults implements ComponentInterface {
    /** The container DOM element */
    @Element() domNode: HTMLUxIwsSearchResultsElement;

    /** The search result category */
    @Prop() category: string = "";

    /** API endpoint from which html content for current filters and sorting is requested */
    @Prop() url: string = "";

    @Listen("uxFilterChange")
    onUxFilterChange(e: CustomEvent) {
        this.formHandler(e);
    }

    /** Storage for the UX filter */
    private filter: HTMLElement | null = null;

    /** Storage for the filter form domNode */
    private filterForm: HTMLFormElement | null = null;

    /** Storage for the sort form domNode */
    private sortForm: HTMLFormElement | null = null;

    /** Send the API request and handle response if url has not changed*/
    private requestData = () => {
        const { url } = this;
        if (url === "") return;
        const query = [
            serializeForm(this.filterForm),
            serializeForm(this.sortForm),
        ].join("&");
        if (isMockUrl(url)) {
            console.log("query", query);
        }
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body: query,
                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };
        api<string>(url, options)
            .then((html) => {
                this.replaceResults(html);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    private requestDataDebounced = debounce(this.requestData, 20);

    private getResultsContainer = (): HTMLDivElement => {
        return this.domNode.querySelector(
            `.${baseClass}__results-container`
        ) as HTMLDivElement;
    };

    private requestInitialData = () => {
        const resultsContainer = this.getResultsContainer();
        if (resultsContainer === null) {
            return;
        }
        const containerItems = [...resultsContainer.childNodes].filter((node) =>
            node.nodeName.toLowerCase().startsWith("ux-iws")
        );
        if (containerItems.length === 0) {
            this.requestDataDebounced();
        }
    };

    private formHandler = (e: Event) => {
        if (e && e.type === "submit") {
            e.preventDefault();
        }
        this.requestDataDebounced();
    };

    private connectFilter() {
        this.filter = this.domNode.querySelector<HTMLElement>(".ux-filter");
        if (this.filter !== null) {
            waitForHydrated(this.filter, () => {
                this.connectFilterForm();
            });
        }
    }

    private connectFilterForm = () => {
        this.disconnectForm(this.filterForm);
        this.filterForm = this.domNode.querySelector(
            ".ux-filter form"
        ) as HTMLFormElement;
        this.connectForm(this.filterForm);
    };

    private connectSortForm = () => {
        this.disconnectForm(this.sortForm);
        this.sortForm = this.domNode.querySelector(
            ".ux-iws-search-results__sort-container form"
        ) as HTMLFormElement;
        this.connectForm(this.sortForm);
    };

    private connectForm = (form: HTMLFormElement | null) => {
        if (form !== null) {
            form.addEventListener("change", this.formHandler);
            form.addEventListener("submit", this.formHandler);
        }
    };

    private disconnectForm = (form: HTMLFormElement | null) => {
        if (form !== null) {
            form.removeEventListener("change", this.formHandler);
            form.removeEventListener("submit", this.formHandler);
        }
    };

    private replaceResults = (html: string | undefined) => {
        if (typeof html !== "string" || html.length === 0) {
            return;
        }
        const resultsContainer = this.getResultsContainer();
        if (resultsContainer === null) {
            return;
        }
        resultsContainer.style.height = `${resultsContainer.offsetHeight}px`;
        resultsContainer.innerHTML = html;
        setTimeout(() => {
            resultsContainer.style.height = "auto";
        }, 200);
    };

    componentDidLoad() {
        this.connectFilter();
        this.connectSortForm();
        this.requestInitialData();
    }

    disconnectedCallback() {
        this.disconnectForm(this.filterForm);
        this.disconnectForm(this.sortForm);
    }

    render() {
        return (
            <Host class={baseClass}>
                <div class={`${baseClass}__inner`}>
                    <slot />
                </div>
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}
